import { useGetUploadSignedUrlForExamCourseAttachDocumentLazyQuery } from '@exam-center/graphql';
import { useLogger } from '@shared/logger';
import { useState } from 'react';

interface UseUploadExamCourseSettingsFileAttachmentParams {
  examCourseId: string;
}

export default function useUploadExamCourseSettingsFileAttachment(
  params: Readonly<UseUploadExamCourseSettingsFileAttachmentParams>,
) {
  const { examCourseId } = params;

  const logger = useLogger();

  const [getUploadSignedUrls, { loading: isGettingUploadSignedUrl }] =
    useGetUploadSignedUrlForExamCourseAttachDocumentLazyQuery({});

  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = async (file: File) => {
    setIsLoading(true);

    if (!file) {
      setIsLoading(false);
      return null;
    }

    const { data: getUploadSignedUrlResult } = await getUploadSignedUrls({
      variables: {
        getUploadSignedUrlForExamCourseAttachDocumentInput: {
          examCourseId,
        },
      },
      fetchPolicy: 'no-cache',
      onError: (error) => {
        logger.error('Failed to get upload signed URL', { error });
        setIsLoading(false);
      },
    });

    const uploadSignedUrl =
      getUploadSignedUrlResult?.ecGetUploadSignedUrlForExamCourseAttachDocument.url;
    const uploadFileName =
      getUploadSignedUrlResult?.ecGetUploadSignedUrlForExamCourseAttachDocument.storageObject
        .fileName;

    if (!uploadSignedUrl || !uploadFileName) {
      setIsLoading(false);
      throw new Error(
        'Failed to get upload signed URL or file name: No signed URL or file name returned',
      );
    }

    try {
      const uploadResponse = await fetch(uploadSignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${file.name}`);
      }

      return {
        originalFileName: file.name,
        uploadFileName: uploadFileName,
      };
    } catch (error) {
      logger.error('Failed to upload file', { error });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { handleUpload, loading: isGettingUploadSignedUrl || isLoading };
}
